import { createContext, useCallback, useContext, useState } from "react";
import { ConfirmDialogProps, ConfirmDialogPropsScroll, ConfirmDialogPropsSize, PageState, SnackbarColor } from "./types"
import { useSnackbar } from "notistack";
import { ResultObj } from "./Domain";
import { Box, Button, IconButton } from "@material-ui/core";
import { Close, Visibility } from "@material-ui/icons";

export interface MainPageModalData {
    title?: string; // 標題
    content: React.ReactNode; // 內容
    onConfirm?: () => void// 確認回調
    onCancel?: () => void// 取消回調
    positiveBtnText?: string //確認按鈕自定義文字
    negativeBtnText?: string //取消按鈕自定義文字
}
export interface MainPageDialogContextInterface {
    mainPageDialogInfo: PageState['dialogInfo'],
    mainPageDialogOpen: (dialogInfo: PageState['dialogInfo']) => void,
    mainPageDialogClose: () => void,
    customMainPageModalOpen: (payload: ConfirmDialogProps) => void,
    mainPageModal: PageState['modal'],
    mainPageModalOpen: (payload: MainPageModalData) => void,
    mainPageModalClose: () => void,
    responseMessage: (res: ResultObj & { persist?: boolean }) => Promise<void>;
}

export const MainPageDialogContext = createContext<MainPageDialogContextInterface>({
    mainPageDialogInfo: null,
    mainPageDialogOpen: () => { },
    mainPageDialogClose: () => { },
    customMainPageModalOpen: () => { },
    mainPageModal: null,
    mainPageModalOpen: (payload: MainPageModalData) => { },
    mainPageModalClose: () => { },
    responseMessage: (res: ResultObj & { persist?: boolean }) => Promise.resolve(),
});

export const MainPageDialogProvider = ({ children }: { children: React.ReactNode }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [mainPageDialogInfo, setMainPageDialogInfo] = useState<PageState['dialogInfo']>(null);
    const [mainPageModal, setMainPageModal] = useState<PageState['modal']>(null);

    /**
     * @description 此Dialog只用於snackbar錯誤信息顯示
     */
    const mainPageDialogOpen = (dialogInfo: PageState['dialogInfo']) => {
        setMainPageDialogInfo(dialogInfo);
    };

    const mainPageDialogClose = () => {
        setMainPageDialogInfo(null);
    };

    /**
     * @description 此modal只用於二次確認訊息，ex 確認要刪除嗎？，確認修改嗎？
     * @param {MainPageModalData} payload
     * @param {string} payload.title 標題
     * @param {React.ReactNode} payload.content 內容
     * @param {function} payload.onConfirm 確認回調,有值的話會顯示確認按鈕，沒有就不會顯示確認按鈕
     * @example mainPageModalOpen({ title: '測試標題', content: '測試內容', onConfirm: () => { console.log('確認按鈕被點擊') } })
     */
    const mainPageModalOpen = (payload: MainPageModalData) => {

        const {
            title = '訊息',
            content = '',
            onConfirm,
            onCancel,
            positiveBtnText = '確認',
            negativeBtnText = '取消',
        } = payload;

        setMainPageModal({
            id: "confirmModal",
            modaltitle: title,
            size: ConfirmDialogPropsSize.XS,
            scroll: ConfirmDialogPropsScroll.BODY,
            // modalAction: onConfirm,
            messages:
                <Box>
                    {content}
                </Box>,
            modalBtnGroup: <Box display="flex" justifyContent="space-between" width="100%" >
                <Button color="primary" type="button" onClick={() => {
                    onCancel && onCancel();
                    mainPageModalClose();
                }}>
                    {negativeBtnText}
                </Button>
                {
                    onConfirm && <Button className="MuiOtherButton" color="primary" type="button"
                        onClick={onConfirm}>
                        {positiveBtnText}
                    </Button>
                }
            </Box >
        });
    };

    /**
     * 關閉modal
     */
    const mainPageModalClose = () => {
        setMainPageModal(null);
    };

    /**
     * @description 以防萬一會有一些得自定義操作畫面情況 
     * @description 想要關閉客制化modal請使用mainPageModalClose
     * @warning 不建議使用這個方法來建立特定頁面高度客制化的modal（ex：modalInnerCreate等），1.因爲這個modal只有一個，沒法達成開啓多個modal的任務,2.因爲他沒法動態渲染，會導致content裏依賴與parent的state沒法觸發rerender
     */
    const customMainPageModalOpen = (payload: ConfirmDialogProps) => {
        setMainPageModal(payload)
    }

    /** API 錯誤處理
    * @param res {ResultObj} API 返回的結果對象,error屬性如果是"DEV_ERROR"的話，可以設定此錯誤提示只在開發環境顯示
    * @example responseMessage(error as ResultObj) // 正常使用
    * @example responseMessage({ ...(error as ResultObj), error: "DEV_ERROR" } as ResultObj)// 只在開發環境顯示
    */
    const responseMessage = useCallback(async (res: ResultObj & { persist?: boolean }) => {
        //在正式不要顯示被指定爲開發錯誤的訊息
        if (res.error === "DEV_ERROR" && process.env.REACT_APP_ENV !== "development") {
            console.error(res);
        } else {
            enqueueSnackbar(res.message, {
                variant: res.success ? SnackbarColor.success : SnackbarColor.warning, // 顏色
                persist: res.persist || !res.success, // 是否要一直存在畫面
                action: key => ( // 錯誤通知 + 查看詳細
                    <>
                        {res.data &&
                            <IconButton onClick={() => {
                                //開啓Dialog
                                mainPageDialogOpen(res);
                            }}>
                                <Visibility style={{ color: "#fff" }} />
                            </IconButton>
                        }
                        <IconButton onClick={() => { closeSnackbar(key) }}>
                            <Close style={{ color: "#fff" }} />
                        </IconButton>
                    </>
                )
            });
        }

    }, [enqueueSnackbar, closeSnackbar])

    return <MainPageDialogContext.Provider value={{
        mainPageDialogInfo,
        mainPageDialogOpen,
        mainPageDialogClose,
        customMainPageModalOpen,
        mainPageModal,
        mainPageModalOpen,
        mainPageModalClose,
        responseMessage,
    }}>
        {children}
    </MainPageDialogContext.Provider>
}

export const WithMainPageDialogContext = (Component: React.ComponentType<any>) => {
    const WrappedWithContext = (props: any) => {
        const context = useContext(MainPageDialogContext);
        return <Component {...props} {...context} />;
    };
    return WrappedWithContext;
};
